"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { AlertTriangle, RefreshCw, X } from "lucide-react"
import posthog from 'posthog-js'
import { isWebpackError } from "@/utils/webpack-error-detector"
import { normalizeError } from "@/types/error-boundary"
import { useTranslations } from "next-intl"

interface WebpackErrorHandlerProps {
  onClose?: () => void
}

export function WebpackErrorHandler({ onClose }: WebpackErrorHandlerProps) {
  const t = useTranslations("common")
  const [showError, setShowError] = useState(false)
  const [countdown, setCountdown] = useState(10)
  const [autoRefresh, setAutoRefresh] = useState(true)

  useEffect(() => {

    const handleError = (event: ErrorEvent) => {
      if (isWebpackError(event.error)) {
        console.error("Webpack module loading error detected:", event.error)
        
        posthog.captureException(event.error, {
          $set: { 
            error_type: "webpack_module_loading",
            error_context: "after_deployment"
          }
        })
        
        setShowError(true)
      }
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isWebpackError(event.reason)) {
        console.error("Webpack module loading error in promise:", event.reason)
        
        posthog.captureException(normalizeError(event.reason), {
          $set: { 
            error_type: "webpack_module_loading_promise",
            error_context: "after_deployment"
          }
        })
        
        setShowError(true)
        event.preventDefault()
      }
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  useEffect(() => {
    if (showError && autoRefresh && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(prev => prev - 1)
      }, 1000)

      return () => clearTimeout(timer)
    }

    if (showError && autoRefresh && countdown === 0) {
      window.location.reload()
    }
    
    return undefined
  }, [showError, autoRefresh, countdown])

  const handleManualRefresh = () => {
    posthog.capture('webpack_error_manual_refresh')
    window.location.reload()
  }

  const handleDismiss = () => {
    setShowError(false)
    setAutoRefresh(false)
    onClose?.()
  }

  if (!showError) return null

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border rounded-lg shadow-lg max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-destructive/10 rounded-full p-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
          </div>
          <div>
            <h3 className="font-semibold">{t("appUpdate.title")}</h3>
            <p className="text-sm text-muted-foreground">
              {t("appUpdate.subtitle")}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="ml-auto"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="mb-6 space-y-2">
          <p className="text-sm">
            {t("appUpdate.description")}
          </p>
          
          {autoRefresh && (
            <div className="bg-muted rounded-md p-3">
              <p className="text-sm font-medium">
                {t("appUpdate.autoRefresh", { countdown })}
              </p>
              <div className="w-full bg-background rounded-full h-2 mt-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${((10 - countdown) / 10) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button onClick={handleManualRefresh} className="flex-1">
            <RefreshCw className="h-4 w-4 mr-2" />
            {t("appUpdate.refresh")}
          </Button>
          <Button
            variant="outline"
            onClick={() => setAutoRefresh(false)}
            className="px-3"
          >
            {t("actions.cancel")}
          </Button>
        </div>
      </div>
    </div>
  )
}